// Interfaces
import { IPayPlansAccesses, IPositionAccess, IWashoutsAccesses } from "./interfaces";

// App Auth Actions
import ACTIONS from "@/Constants/actionsList";

// Helpers
import { getDepartmentalPosition, supportedSystemPositions } from "@Common/helpers/Positions";

const POSITIONS = supportedSystemPositions();

const defaultAccess = {
	payPlans: {
		view: "",
		edit: "",
		viewAssignments: "",
		editAssignments: "",
	},
	washouts: {
		view: "",
		override: "",
		review: "",
	},
};

const ACCESS_MAP = new Map([
	[POSITIONS.LOCATION.GENERAL_MANAGER, { ...defaultAccess }],
	// SALES
	[
		POSITIONS.SALES.SALES_ASSOCIATE,
		{
			payPlans: {
				view: ACTIONS.VIEW_SALES_ASSOCIATE_PAY_PLANS,
				edit: ACTIONS.EDIT_SALES_ASSOCIATE_PAY_PLANS,
				viewAssignments: ACTIONS.VIEW_SALES_ASSOCIATE_PAY_PLAN_ASSIGNMENTS,
				editAssignments: ACTIONS.EDIT_SALES_ASSOCIATE_PAY_PLAN_ASSIGNMENTS,
			},
			washouts: {
				view: ACTIONS.VIEW_SALES_ASSOCIATE_WASHOUTS,
				override: ACTIONS.OVERRIDE_SALES_ASSOCIATE_COMPENSATION,
				review: ACTIONS.REVIEW_SALES_ASSOCIATE_WASHOUTS,
			},
		},
	],
	[
		POSITIONS.SALES.SALES_MANAGER,
		{
			payPlans: {
				view: ACTIONS.VIEW_SALES_MANAGER_PAY_PLANS,
				edit: ACTIONS.EDIT_SALES_MANAGER_PAY_PLANS,
				viewAssignments: ACTIONS.VIEW_SALES_MANAGER_PAY_PLAN_ASSIGNMENTS,
				editAssignments: ACTIONS.EDIT_SALES_MANAGER_PAY_PLAN_ASSIGNMENTS,
			},
			washouts: {
				view: ACTIONS.VIEW_SALES_MANAGER_WASHOUTS,
				override: ACTIONS.OVERRIDE_SALES_MANAGER_COMPENSATION,
				review: ACTIONS.REVIEW_SALES_MANAGER_WASHOUTS,
			},
		},
	],
	[
		POSITIONS.SALES.GENERAL_SALES_MANAGER,
		{
			payPlans: {
				view: ACTIONS.VIEW_GENERAL_SALES_MANAGER_PAY_PLANS,
				edit: ACTIONS.EDIT_GENERAL_SALES_MANAGER_PAY_PLANS,
				viewAssignments: ACTIONS.VIEW_GENERAL_SALES_MANAGER_PAY_PLAN_ASSIGNMENTS,
				editAssignments: ACTIONS.EDIT_GENERAL_SALES_MANAGER_PAY_PLAN_ASSIGNMENTS,
			},
			washouts: {
				view: ACTIONS.VIEW_GENERAL_SALES_MANAGER_WASHOUTS,
				override: ACTIONS.OVERRIDE_GENERAL_SALES_MANAGER_COMPENSATION,
				review: ACTIONS.REVIEW_GENERAL_SALES_MANAGER_WASHOUTS,
			},
		},
	],
	// FINANCE
	[
		POSITIONS.FINANCE.FINANCE_MANAGER,
		{
			payPlans: {
				view: ACTIONS.VIEW_FINANCE_MANAGER_PAY_PLANS,
				edit: ACTIONS.EDIT_FINANCE_MANAGER_PAY_PLANS,
				viewAssignments: ACTIONS.VIEW_FINANCE_MANAGER_PAY_PLAN_ASSIGNMENTS,
				editAssignments: ACTIONS.EDIT_FINANCE_MANAGER_PAY_PLAN_ASSIGNMENTS,
			},
			washouts: {
				view: ACTIONS.VIEW_FINANCE_MANAGER_WASHOUTS,
				override: ACTIONS.OVERRIDE_FINANCE_MANAGER_COMPENSATION,
				review: ACTIONS.REVIEW_FINANCE_MANAGER_WASHOUTS,
			},
		},
	],
	[
		POSITIONS.FINANCE.FINANCE_DIRECTOR,
		{
			payPlans: {
				view: ACTIONS.VIEW_FINANCE_DIRECTOR_PAY_PLANS,
				edit: ACTIONS.EDIT_FINANCE_DIRECTOR_PAY_PLANS,
				viewAssignments: ACTIONS.VIEW_FINANCE_DIRECTOR_PAY_PLAN_ASSIGNMENTS,
				editAssignments: ACTIONS.EDIT_FINANCE_DIRECTOR_PAY_PLAN_ASSIGNMENTS,
			},
			washouts: {
				view: ACTIONS.VIEW_FINANCE_DIRECTOR_WASHOUTS,
				override: ACTIONS.OVERRIDE_FINANCE_DIRECTOR_COMPENSATION,
				review: ACTIONS.REVIEW_FINANCE_DIRECTOR_WASHOUTS,
			},
		},
	],

	// BDC
	[
		POSITIONS.BDC.BDC_SALES_ASSOCIATE,
		{
			payPlans: {
				view: ACTIONS.VIEW_BDC_ASSOCIATE_PAY_PLANS,
				edit: ACTIONS.EDIT_BDC_ASSOCIATE_PAY_PLANS,
				viewAssignments: ACTIONS.VIEW_BDC_ASSOCIATE_PAY_PLAN_ASSIGNMENTS,
				editAssignments: ACTIONS.EDIT_BDC_ASSOCIATE_PAY_PLAN_ASSIGNMENTS,
			},
			washouts: {
				view: ACTIONS.VIEW_BDC_ASSOCIATE_WASHOUTS,
				override: ACTIONS.OVERRIDE_BDC_ASSOCIATE_COMPENSATION,
				review: ACTIONS.REVIEW_BDC_ASSOCIATE_WASHOUTS,
			},
		},
	],
	[POSITIONS.BDC.BDC_SERVICE_ASSOCIATE, { ...defaultAccess }],
	[
		POSITIONS.BDC.BDC_MANAGER,
		{
			payPlans: {
				view: ACTIONS.VIEW_BDC_MANAGER_PAY_PLANS,
				edit: ACTIONS.EDIT_BDC_MANAGER_PAY_PLANS,
				viewAssignments: ACTIONS.VIEW_BDC_MANAGER_PAY_PLAN_ASSIGNMENTS,
				editAssignments: ACTIONS.EDIT_BDC_MANAGER_PAY_PLAN_ASSIGNMENTS,
			},
			washouts: {
				view: ACTIONS.VIEW_BDC_MANAGER_WASHOUTS,
				override: ACTIONS.OVERRIDE_BDC_MANAGER_COMPENSATION,
				review: ACTIONS.REVIEW_BDC_MANAGER_WASHOUTS,
			},
		},
	],

	// SERVICE
	[
		POSITIONS.SERVICE.SERVICE_TECHNICIAN,
		{
			payPlans: {
				view: ACTIONS.VIEW_SERVICE_TECHNICIAN_PAY_PLANS,
				edit: ACTIONS.EDIT_SERVICE_TECHNICIAN_PAY_PLANS,
				viewAssignments: ACTIONS.VIEW_SERVICE_TECHNICIAN_PAY_PLAN_ASSIGNMENTS,
				editAssignments: ACTIONS.EDIT_SERVICE_TECHNICIAN_PAY_PLAN_ASSIGNMENTS,
			},
			washouts: {
				view: ACTIONS.VIEW_SERVICE_TECHNICIAN_WASHOUTS,
				override: ACTIONS.OVERRIDE_SERVICE_TECHNICIAN_COMPENSATION,
				review: ACTIONS.REVIEW_SERVICE_TECHNICIAN_WASHOUTS,
			},
		},
	],
	[
		POSITIONS.SERVICE.SERVICE_ADVISOR,
		{
			payPlans: {
				view: ACTIONS.VIEW_SERVICE_ADVISOR_PAY_PLANS,
				edit: ACTIONS.EDIT_SERVICE_ADVISOR_PAY_PLANS,
				viewAssignments: ACTIONS.VIEW_SERVICE_ADVISOR_PAY_PLAN_ASSIGNMENTS,
				editAssignments: ACTIONS.EDIT_SERVICE_ADVISOR_PAY_PLAN_ASSIGNMENTS,
			},
			washouts: {
				view: ACTIONS.VIEW_SERVICE_ADVISOR_WASHOUTS,
				override: ACTIONS.OVERRIDE_SERVICE_ADVISOR_COMPENSATION,
				review: ACTIONS.REVIEW_SERVICE_ADVISOR_WASHOUTS,
			},
		},
	],
	[
		POSITIONS.SERVICE.SERVICE_MANAGER,
		{
			payPlans: {
				view: ACTIONS.VIEW_SERVICE_MANAGER_PAY_PLANS,
				edit: ACTIONS.EDIT_SERVICE_MANAGER_PAY_PLANS,
				viewAssignments: ACTIONS.VIEW_SERVICE_MANAGER_PAY_PLAN_ASSIGNMENTS,
				editAssignments: ACTIONS.EDIT_SERVICE_MANAGER_PAY_PLAN_ASSIGNMENTS,
			},
			washouts: {
				view: ACTIONS.VIEW_SERVICE_MANAGER_WASHOUTS,
				override: ACTIONS.OVERRIDE_SERVICE_MANAGER_COMPENSATION,
				review: ACTIONS.REVIEW_SERVICE_MANAGER_WASHOUTS,
			},
		},
	],
	[
		POSITIONS.SERVICE.SERVICE_DIRECTOR,
		{
			payPlans: {
				view: ACTIONS.VIEW_SERVICE_DIRECTOR_PAY_PLANS,
				edit: ACTIONS.EDIT_SERVICE_DIRECTOR_PAY_PLANS,
				viewAssignments: ACTIONS.VIEW_SERVICE_DIRECTOR_PAY_PLAN_ASSIGNMENTS,
				editAssignments: ACTIONS.EDIT_SERVICE_DIRECTOR_PAY_PLAN_ASSIGNMENTS,
			},
			washouts: {
				view: ACTIONS.VIEW_SERVICE_DIRECTOR_WASHOUTS,
				override: ACTIONS.OVERRIDE_SERVICE_DIRECTOR_COMPENSATION,
				review: ACTIONS.REVIEW_SERVICE_DIRECTOR_WASHOUTS,
			},
		},
	],
	[
		POSITIONS.SERVICE.SERVICE_FOREMAN,
		{
			payPlans: {
				view: ACTIONS.VIEW_SERVICE_FOREMAN_PAY_PLANS,
				edit: ACTIONS.EDIT_SERVICE_FOREMAN_PAY_PLANS,
				viewAssignments: ACTIONS.VIEW_SERVICE_FOREMAN_PAY_PLAN_ASSIGNMENTS,
				editAssignments: ACTIONS.EDIT_SERVICE_FOREMAN_PAY_PLAN_ASSIGNMENTS,
			},
			washouts: {
				view: ACTIONS.VIEW_SERVICE_FOREMAN_WASHOUTS,
				override: ACTIONS.OVERRIDE_SERVICE_FOREMAN_COMPENSATION,
				review: ACTIONS.REVIEW_SERVICE_FOREMAN_WASHOUTS,
			},
		},
	],
	[
		POSITIONS.SERVICE.INTERNAL_SERVICE_ADVISOR,
		{
			payPlans: {
				view: ACTIONS.VIEW_INTERNAL_SERVICE_ADVISOR_PAY_PLANS,
				edit: ACTIONS.EDIT_INTERNAL_SERVICE_ADVISOR_PAY_PLANS,
				viewAssignments: ACTIONS.VIEW_INTERNAL_SERVICE_ADVISOR_PAY_PLAN_ASSIGNMENTS,
				editAssignments: ACTIONS.EDIT_INTERNAL_SERVICE_ADVISOR_PAY_PLAN_ASSIGNMENTS,
			},
			washouts: {
				view: ACTIONS.VIEW_INTERNAL_SERVICE_ADVISOR_WASHOUTS,
				override: ACTIONS.OVERRIDE_INTERNAL_SERVICE_ADVISOR_COMPENSATION,
				review: ACTIONS.REVIEW_INTERNAL_SERVICE_ADVISOR_WASHOUTS,
			},
		},
	],

	// BODY SHOP
	[
		POSITIONS.BODY_SHOP.BODY_SHOP_MANAGER,
		{
			payPlans: {
				view: ACTIONS.VIEW_BODY_SHOP_MANAGER_PAY_PLANS,
				edit: ACTIONS.EDIT_BODY_SHOP_MANAGER_PAY_PLANS,
				viewAssignments: ACTIONS.VIEW_BODY_SHOP_MANAGER_PAY_PLAN_ASSIGNMENTS,
				editAssignments: ACTIONS.EDIT_BODY_SHOP_MANAGER_PAY_PLAN_ASSIGNMENTS,
			},
			washouts: {
				view: ACTIONS.VIEW_BODY_SHOP_MANAGER_WASHOUTS,
				override: ACTIONS.OVERRIDE_BODY_SHOP_MANAGER_COMPENSATION,
				review: ACTIONS.REVIEW_BODY_SHOP_MANAGER_WASHOUTS,
			},
		},
	],
	[
		POSITIONS.BODY_SHOP.BODY_SHOP_PRODUCT_MANAGER,
		{
			payPlans: {
				view: ACTIONS.VIEW_BODY_SHOP_PRODUCT_MANAGER_PAY_PLANS,
				edit: ACTIONS.EDIT_BODY_SHOP_PRODUCT_MANAGER_PAY_PLANS,
				viewAssignments: ACTIONS.VIEW_BODY_SHOP_PRODUCT_MANAGER_PAY_PLAN_ASSIGNMENTS,
				editAssignments: ACTIONS.EDIT_BODY_SHOP_PRODUCT_MANAGER_PAY_PLAN_ASSIGNMENTS,
			},
			washouts: {
				view: ACTIONS.VIEW_BODY_SHOP_PRODUCT_MANAGER_WASHOUTS,
				override: ACTIONS.OVERRIDE_BODY_SHOP_PRODUCT_MANAGER_COMPENSATION,
				review: ACTIONS.REVIEW_BODY_SHOP_PRODUCT_MANAGER_WASHOUTS,
			},
		},
	],
	[
		POSITIONS.BODY_SHOP.BODY_SHOP_FOREMAN,
		{
			payPlans: {
				view: ACTIONS.VIEW_BODY_SHOP_FOREMAN_PAY_PLANS,
				edit: ACTIONS.EDIT_BODY_SHOP_FOREMAN_PAY_PLANS,
				viewAssignments: ACTIONS.VIEW_BODY_SHOP_FOREMAN_PAY_PLAN_ASSIGNMENTS,
				editAssignments: ACTIONS.EDIT_BODY_SHOP_FOREMAN_PAY_PLAN_ASSIGNMENTS,
			},
			washouts: {
				view: ACTIONS.VIEW_BODY_SHOP_FOREMAN_WASHOUTS,
				override: ACTIONS.OVERRIDE_BODY_SHOP_FOREMAN_COMPENSATION,
				review: ACTIONS.REVIEW_BODY_SHOP_FOREMAN_WASHOUTS,
			},
		},
	],
	[
		POSITIONS.BODY_SHOP.BODY_SHOP_TECHNICIAN,
		{
			payPlans: {
				view: ACTIONS.VIEW_BODY_SHOP_TECHNICIAN_PAY_PLANS,
				edit: ACTIONS.EDIT_BODY_SHOP_TECHNICIAN_PAY_PLANS,
				viewAssignments: ACTIONS.VIEW_BODY_SHOP_TECHNICIAN_PAY_PLAN_ASSIGNMENTS,
				editAssignments: ACTIONS.EDIT_BODY_SHOP_TECHNICIAN_PAY_PLAN_ASSIGNMENTS,
			},
			washouts: {
				view: ACTIONS.VIEW_BODY_SHOP_TECHNICIAN_WASHOUTS,
				override: ACTIONS.OVERRIDE_BODY_SHOP_TECHNICIAN_COMPENSATION,
				review: ACTIONS.REVIEW_BODY_SHOP_TECHNICIAN_WASHOUTS,
			},
		},
	],
	[
		POSITIONS.BODY_SHOP.BODY_SHOP_PAINTER,
		{
			payPlans: {
				view: ACTIONS.VIEW_BODY_SHOP_PAINTER_PAY_PLANS,
				edit: ACTIONS.EDIT_BODY_SHOP_PAINTER_PAY_PLANS,
				viewAssignments: ACTIONS.VIEW_BODY_SHOP_PAINTER_PAY_PLAN_ASSIGNMENTS,
				editAssignments: ACTIONS.EDIT_BODY_SHOP_PAINTER_PAY_PLAN_ASSIGNMENTS,
			},
			washouts: {
				view: ACTIONS.VIEW_BODY_SHOP_PAINTER_WASHOUTS,
				override: ACTIONS.OVERRIDE_BODY_SHOP_PAINTER_COMPENSATION,
				review: ACTIONS.REVIEW_BODY_SHOP_PAINTER_WASHOUTS,
			},
		},
	],
	[
		POSITIONS.BODY_SHOP.BODY_SHOP_ESTIMATOR,
		{
			payPlans: {
				view: ACTIONS.VIEW_BODY_SHOP_ESTIMATOR_PAY_PLANS,
				edit: ACTIONS.EDIT_BODY_SHOP_ESTIMATOR_PAY_PLANS,
				viewAssignments: ACTIONS.VIEW_BODY_SHOP_ESTIMATOR_PAY_PLAN_ASSIGNMENTS,
				editAssignments: ACTIONS.EDIT_BODY_SHOP_ESTIMATOR_PAY_PLAN_ASSIGNMENTS,
			},
			washouts: {
				view: ACTIONS.VIEW_BODY_SHOP_ESTIMATOR_WASHOUTS,
				override: ACTIONS.OVERRIDE_BODY_SHOP_ESTIMATOR_COMPENSATION,
				review: ACTIONS.REVIEW_BODY_SHOP_ESTIMATOR_WASHOUTS,
			},
		},
	],

	// PARTS
	[
		POSITIONS.PARTS.PARTS_COUNTER_ASSOCIATE,
		{
			payPlans: {
				view: ACTIONS.VIEW_PARTS_COUNTER_ASSOCIATE_PAY_PLANS,
				edit: ACTIONS.EDIT_PARTS_COUNTER_ASSOCIATE_PAY_PLANS,
				viewAssignments: ACTIONS.VIEW_PARTS_COUNTER_ASSOCIATE_PAY_PLAN_ASSIGNMENTS,
				editAssignments: ACTIONS.EDIT_PARTS_COUNTER_ASSOCIATE_PAY_PLAN_ASSIGNMENTS,
			},
			washouts: {
				view: ACTIONS.VIEW_PARTS_COUNTER_ASSOCIATE_WASHOUTS,
				override: ACTIONS.OVERRIDE_PARTS_COUNTER_ASSOCIATE_COMPENSATION,
				review: ACTIONS.REVIEW_PARTS_COUNTER_ASSOCIATE_WASHOUTS,
			},
		},
	],
	[
		POSITIONS.PARTS.PARTS_MANAGER,
		{
			payPlans: {
				view: ACTIONS.VIEW_PARTS_MANAGER_PAY_PLANS,
				edit: ACTIONS.EDIT_PARTS_MANAGER_PAY_PLANS,
				viewAssignments: ACTIONS.VIEW_PARTS_MANAGER_PAY_PLAN_ASSIGNMENTS,
				editAssignments: ACTIONS.EDIT_PARTS_MANAGER_PAY_PLAN_ASSIGNMENTS,
			},
			washouts: {
				view: ACTIONS.VIEW_PARTS_MANAGER_WASHOUTS,
				override: ACTIONS.OVERRIDE_PARTS_MANAGER_COMPENSATION,
				review: ACTIONS.REVIEW_PARTS_MANAGER_WASHOUTS,
			},
		},
	],
	[
		POSITIONS.PARTS.PARTS_DIRECTOR,
		{
			payPlans: {
				view: ACTIONS.VIEW_PARTS_DIRECTOR_PAY_PLANS,
				edit: ACTIONS.EDIT_PARTS_DIRECTOR_PAY_PLANS,
				viewAssignments: ACTIONS.VIEW_PARTS_DIRECTOR_PAY_PLAN_ASSIGNMENTS,
				editAssignments: ACTIONS.EDIT_PARTS_DIRECTOR_PAY_PLAN_ASSIGNMENTS,
			},
			washouts: {
				view: ACTIONS.VIEW_PARTS_DIRECTOR_WASHOUTS,
				override: ACTIONS.OVERRIDE_PARTS_DIRECTOR_COMPENSATION,
				review: ACTIONS.REVIEW_PARTS_DIRECTOR_WASHOUTS,
			},
		},
	],
]);

const getPositionsAccessMap = () => ACCESS_MAP;

const getPositionAccess = (position: string): IPositionAccess => {
	const departmental = getDepartmentalPosition(position);
	return ACCESS_MAP.get(departmental) || defaultAccess;
};

const getPositionsAccesses = (positions: string[], accesses?: IPositionAccess): IPositionAccess[] => {
	if (!accesses) {
		return positions.map((position) => getPositionAccess(position));
	}

	const getSpecificAccess = (positionAccesses: IPositionAccess, params: IPositionAccess) => {
		return Object.keys(params).reduce((acc, key: string) => {
			const accessParams = params[key as keyof IPositionAccess];
			acc[key as keyof IPositionAccess] = Object.keys(accessParams as IPayPlansAccesses | IWashoutsAccesses).reduce(
				(accessAcc, actionKey: string) => {
					accessAcc[actionKey as keyof IPayPlansAccesses | keyof IWashoutsAccesses] =
						positionAccesses[key as keyof IPositionAccess]?.[actionKey as keyof IPayPlansAccesses & keyof IWashoutsAccesses] ||
						"";
					return accessAcc;
				},
				{} as IPayPlansAccesses & IWashoutsAccesses
			);
			return acc;
		}, {} as IPositionAccess);
	};

	return positions.map((position) => {
		const positionAccesses = getPositionAccess(position);
		return getSpecificAccess(positionAccesses, accesses);
	});
};

const getPayPlansEditAccess = (position: string): string => {
	const access = getPositionAccess(position);
	return String(access.payPlans?.edit) || "";
};

const getPayPlansViewAccess = (position: string): string => {
	const access = getPositionAccess(position);
	return String(access.payPlans?.view) || "";
};

export { getPayPlansEditAccess, getPayPlansViewAccess, getPositionAccess, getPositionsAccesses, getPositionsAccessMap };
